/* Lafis — Home Accessories · global.js */
(() => {
  'use strict';

  const trapStack = [];
  function trapFocus(container) {
    const focusable = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    trapStack.push({ container, handler });
    first.focus({ preventScroll: true });
  }
  function releaseFocus(container) {
    const idx = trapStack.findIndex((t) => t.container === container);
    if (idx > -1) {
      container.removeEventListener('keydown', trapStack[idx].handler);
      trapStack.splice(idx, 1);
    }
  }
  /* ---------- Scroll lock ----------
     Every overlay in the theme signals "page must not scroll" by toggling
     .overflow-hidden on <body>. On mobile that class alone is not enough, so we
     watch it and pin the body at its current offset while it is set. */
  (() => {
    let lockedY = 0;
    let isLocked = false;

    function lock() {
      if (isLocked) return;
      lockedY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('scroll-locked');
      document.body.style.top = `-${lockedY}px`;
      isLocked = true;
    }
    function unlock() {
      if (!isLocked) return;
      document.documentElement.classList.remove('scroll-locked');
      document.body.style.top = '';
      isLocked = false;
      window.scrollTo(0, lockedY);
    }

    new MutationObserver(() => {
      if (document.body.classList.contains('overflow-hidden')) lock();
      else unlock();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  })();


  /* ---------- Drawers (menu / cart / search) ---------- */
  const overlay = document.querySelector('.drawer-overlay');
  let openDrawer = null;

  function toggleDrawer(id, open) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    const willOpen = open !== undefined ? open : !drawer.classList.contains('is-open');
    if (willOpen && openDrawer && openDrawer !== drawer) closeDrawers();
    drawer.classList.toggle('is-open', willOpen);
    overlay && overlay.classList.toggle('is-open', willOpen);
    document.body.classList.toggle('overflow-hidden', willOpen);
    drawer.setAttribute('aria-hidden', String(!willOpen));
    if (willOpen) { openDrawer = drawer; trapFocus(drawer); }
    else { releaseFocus(drawer); openDrawer = null; }
  }
  function closeDrawers() {
    document.querySelectorAll('.menu-drawer.is-open, .cart-drawer.is-open, .search-drawer.is-open').forEach((d) => {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      releaseFocus(d);
    });
    overlay && overlay.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    openDrawer = null;
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); toggleDrawer(opener.dataset.drawerOpen, true); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { e.preventDefault(); closeDrawers(); }
  });
  overlay && overlay.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawers(); });

  /* ---------- Announcement bar rotation ---------- */
  document.querySelectorAll('[data-announcement-slider]').forEach((bar) => {
    const messages = bar.querySelectorAll('.announcement-bar__message');
    if (messages.length < 2) return;
    let i = 0;
    setInterval(() => {
      messages[i].classList.remove('is-active');
      i = (i + 1) % messages.length;
      messages[i].classList.add('is-active');
    }, 5000);
  });

  /* ---------- Hero slideshow ---------- */
  class Slideshow {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('.slideshow__track');
      this.slides = [...el.querySelectorAll('.slideshow__slide')];
      this.dots = [...el.querySelectorAll('.slideshow__dot')];
      this.autoplay = el.dataset.autoplay === 'true';
      this.speed = (parseInt(el.dataset.speed, 10) || 6) * 1000;
      this.index = 0;
      this.timer = null;
      this.rtl = document.documentElement.dir === 'rtl';
      if (this.slides.length === 0) return;

      el.style.setProperty('--dot-duration', `${this.speed}ms`);
      this.setActive(0);

      el.querySelectorAll('[data-slide-prev]').forEach((b) => b.addEventListener('click', () => this.go(this.index - 1)));
      el.querySelectorAll('[data-slide-next]').forEach((b) => b.addEventListener('click', () => this.go(this.index + 1)));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

      let scrollTimeout;
      this.track.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          const i = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
          if (i !== this.index) this.setActive(Math.min(i, this.slides.length - 1));
        }, 80);
      }, { passive: true });

      ['pointerenter', 'focusin'].forEach((ev) => el.addEventListener(ev, () => this.pause()));
      ['pointerleave', 'focusout'].forEach((ev) => el.addEventListener(ev, () => this.play()));
      this.track.addEventListener('touchstart', () => this.pause(), { passive: true });
      this.track.addEventListener('touchend', () => this.play(), { passive: true });

      new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.isIntersecting ? this.play() : this.pause());
      }, { threshold: 0.25 }).observe(el);
    }
    go(i) {
      const count = this.slides.length;
      const next = ((i % count) + count) % count;
      const offset = this.track.clientWidth * next * (this.rtl ? -1 : 1);
      this.track.scrollTo({ left: offset, behavior: 'smooth' });
      this.setActive(next);
    }
    setActive(i) {
      this.index = i;
      this.slides.forEach((s, j) => {
        s.classList.toggle('is-active', j === i);
        const video = s.querySelector('video');
        if (video) {
          if (j === i) { video.play().catch(() => {}); }
          else { video.pause(); }
        }
      });
      this.dots.forEach((d, j) => {
        d.classList.toggle('is-active', j === i);
        d.setAttribute('aria-current', j === i ? 'true' : 'false');
      });
      this.restart();
    }
    play() {
      if (!this.autoplay || this.slides.length < 2) return;
      this.el.classList.remove('is-paused');
      this.restart();
    }
    pause() {
      this.el.classList.add('is-paused');
      clearInterval(this.timer);
      this.timer = null;
    }
    restart() {
      if (!this.autoplay || this.slides.length < 2 || this.el.classList.contains('is-paused')) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
  }
  document.querySelectorAll('[data-slideshow]').forEach((el) => new Slideshow(el));

  /* ---------- Scroll reveal ---------- */
  let revealObserver = null;
  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -40px 0px' });
  }

  function revealIn(root) {
    const els = (root || document).querySelectorAll('.reveal:not(.is-revealed)');
    if (!revealObserver) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    els.forEach((el) => revealObserver.observe(el));
  }
  revealIn(document);

  // Shopify theme editor re-renders sections without re-running this script:
  // re-scan for un-revealed elements whenever a section loads or is edited.
  document.addEventListener('shopify:section:load', (e) => {
    revealIn(e.target);
    e.target.querySelectorAll('[data-slideshow]').forEach((el) => new Slideshow(el));
  });
  document.addEventListener('shopify:section:select', (e) => revealIn(e.target));
  // Safety net: never leave content hidden if the observer stalls.
  window.addEventListener('load', () => {
    setTimeout(() => document.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 1.5) el.classList.add('is-revealed');
    }), 400);
  });

  /* ---------- Money formatting ---------- */
  function formatMoney(cents) {
    const format = window.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withComma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withComma}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, int.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* ---------- Cart ---------- */
  const Cart = {
    async fetchState() {
      const res = await fetch(`${window.routes.cart_url}.js`);
      return res.json();
    },
    async add(body, button) {
      if (button) button.classList.add('is-loading');
      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || window.cartStrings.error);
        await this.refresh();
        toggleDrawer('CartDrawer', true);
      } catch (err) {
        alert(err.message || window.cartStrings.error);
      } finally {
        if (button) button.classList.remove('is-loading');
      }
    },
    async change(line, quantity) {
      const res = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (res.ok) await this.refresh();
    },
    async refresh() {
      const cart = await this.fetchState();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('hidden', cart.item_count === 0);
      });
      this.renderDrawer(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    },
    renderDrawer(cart) {
      const itemsEl = document.querySelector('[data-cart-drawer-items]');
      const footerEl = document.querySelector('[data-cart-drawer-footer]');
      const emptyEl = document.querySelector('[data-cart-drawer-empty]');
      if (!itemsEl) return;

      const hasItems = cart.item_count > 0;
      itemsEl.classList.toggle('hidden', !hasItems);
      footerEl && footerEl.classList.toggle('hidden', !hasItems);
      emptyEl && emptyEl.classList.toggle('hidden', hasItems);

      itemsEl.innerHTML = cart.items.map((item, i) => `
        <div class="cart-drawer__item" data-line="${i + 1}">
          <a href="${item.url}">${item.image
            ? `<img src="${item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_160x160$1$2')}" alt="" width="80" height="80" loading="lazy">`
            : ''}</a>
          <div>
            <a class="cart-drawer__item-title" href="${item.url}">${item.product_title}</a>
            <div class="cart-drawer__item-meta">
              ${item.variant_title ? `${item.variant_title} · ` : ''}${formatMoney(item.final_price)}
            </div>
            <div class="cart-drawer__item-row">
              <div class="quantity">
                <button type="button" class="quantity__button" data-drawer-qty="-1" aria-label="−">−</button>
                <input class="quantity__input" type="number" value="${item.quantity}" min="0" data-drawer-qty-input aria-label="Quantity">
                <button type="button" class="quantity__button" data-drawer-qty="1" aria-label="+">+</button>
              </div>
              <span class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
        </div>`).join('');

      const subtotalEl = document.querySelector('[data-cart-drawer-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);

      const shippingBar = document.querySelector('[data-free-shipping]');
      if (shippingBar) {
        const threshold = parseInt(shippingBar.dataset.threshold, 10) * 100;
        const remaining = Math.max(0, threshold - cart.items_subtotal_price);
        const pct = Math.min(100, (cart.items_subtotal_price / threshold) * 100);
        shippingBar.querySelector('[data-free-shipping-bar]').style.width = `${pct}%`;
        const msg = shippingBar.querySelector('[data-free-shipping-text]');
        msg.textContent = remaining === 0
          ? shippingBar.dataset.unlockedText
          : shippingBar.dataset.remainingText.replace('[amount]', formatMoney(remaining));
      }
    }
  };

  window.LafisCart = Cart;

  document.addEventListener('click', (e) => {
    const qtyBtn = e.target.closest('[data-drawer-qty]');
    if (qtyBtn) {
      const item = qtyBtn.closest('[data-line]');
      const input = item.querySelector('[data-drawer-qty-input]');
      const next = Math.max(0, parseInt(input.value, 10) + parseInt(qtyBtn.dataset.drawerQty, 10));
      Cart.change(parseInt(item.dataset.line, 10), next);
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-drawer-qty-input]');
    if (input) {
      const item = input.closest('[data-line]');
      Cart.change(parseInt(item.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0));
    }
  });

  /* Quick add buttons (single-variant products) */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    Cart.add({ id: parseInt(btn.dataset.quickAdd, 10), quantity: 1 }, btn);
  });

  /* Product forms (product page) */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    Cart.add({
      id: parseInt(formData.get('id'), 10),
      quantity: parseInt(formData.get('quantity') || '1', 10)
    }, button);
  });

  /* Quantity selectors */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = btn.closest('.quantity').querySelector('.quantity__input');
    const min = parseInt(input.min, 10) || 1;
    const next = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(btn.dataset.qtyChange, 10));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product page: variants & gallery ---------- */
  const productEl = document.querySelector('[data-product]');
  if (productEl) {
    const variants = JSON.parse(productEl.querySelector('[data-variants-json]')?.textContent || '[]');
    const form = productEl.querySelector('form[data-product-form]');
    const idInput = form?.querySelector('input[name="id"]');
    const priceEl = productEl.querySelector('[data-product-price]');
    const submitBtn = form?.querySelector('[type="submit"]');
    const submitLabel = submitBtn?.querySelector('.button__label');

    function currentOptions() {
      return [...productEl.querySelectorAll('.variant-picker fieldset')].map(
        (fs) => fs.querySelector('input:checked')?.value
      );
    }

    function onVariantChange() {
      const opts = currentOptions();
      const variant = variants.find((v) => v.options.every((o, i) => o === opts[i]));
      productEl.querySelectorAll('[data-option-value]').forEach((label) => {
        const fs = label.closest('fieldset');
        const idx = parseInt(fs.dataset.optionIndex, 10);
        const test = [...opts];
        test[idx] = label.dataset.optionValue;
        const match = variants.find((v) => v.options.every((o, i) => o === test[i]));
        label.classList.toggle('is-unavailable', !(match && match.available));
      });

      if (!variant) {
        if (submitBtn) { submitBtn.setAttribute('disabled', ''); if (submitLabel) submitLabel.textContent = window.variantStrings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price > variant.price
          ? `<span class="price__sale-price">${formatMoney(variant.price)}</span> <s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
          : `<span>${formatMoney(variant.price)}</span>`;
      }
      if (submitBtn) {
        if (variant.available) {
          submitBtn.removeAttribute('disabled');
          if (submitLabel) submitLabel.textContent = window.variantStrings.addToCart;
        } else {
          submitBtn.setAttribute('disabled', '');
          if (submitLabel) submitLabel.textContent = window.variantStrings.soldOut;
        }
      }
      if (variant.featured_media) {
        const target = productEl.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (target) activateMedia(target.dataset.mediaId);
      }
    }
    productEl.querySelectorAll('.variant-picker input[type="radio"]').forEach((input) => {
      input.addEventListener('change', onVariantChange);
    });

    const mediaItems = [...productEl.querySelectorAll('.product__media-item')];
    const mediaCounter = productEl.querySelector('[data-media-counter]');
    const imageOpeners = [...productEl.querySelectorAll('[data-media-open]')];
    const lightbox = productEl.querySelector('[data-media-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-lightbox-image]');
    const lightboxCounter = lightbox?.querySelector('[data-lightbox-counter]');
    let lightboxIndex = 0;

    function activeMediaIndex() {
      return Math.max(0, mediaItems.findIndex((m) => m.classList.contains('is-active')));
    }

    function activateMedia(id) {
      let activeIndex = 0;
      mediaItems.forEach((m, i) => {
        const active = m.dataset.mediaId === String(id);
        if (active) activeIndex = i;
        m.classList.toggle('is-active', active);
        const video = m.querySelector('video');
        if (video && !active) video.pause();
      });
      productEl.querySelectorAll('.product__thumbnail').forEach((t) => {
        const active = t.dataset.mediaId === String(id);
        t.classList.toggle('is-active', active);
        if (active) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
      if (mediaCounter) mediaCounter.textContent = `${activeIndex + 1} / ${mediaItems.length}`;
    }

    function stepMedia(dir) {
      if (mediaItems.length < 2) return;
      const next = (activeMediaIndex() + dir + mediaItems.length) % mediaItems.length;
      activateMedia(mediaItems[next].dataset.mediaId);
    }

    productEl.querySelectorAll('.product__thumbnail').forEach((thumb) => {
      thumb.addEventListener('click', () => activateMedia(thumb.dataset.mediaId));
    });
    const prevBtn = productEl.querySelector('[data-media-prev]');
    const nextBtn = productEl.querySelector('[data-media-next]');
    prevBtn && prevBtn.addEventListener('click', () => stepMedia(-1));
    nextBtn && nextBtn.addEventListener('click', () => stepMedia(1));

    function showLightboxImage(index) {
      if (!lightboxImage || imageOpeners.length === 0) return;
      lightboxIndex = (index + imageOpeners.length) % imageOpeners.length;
      const opener = imageOpeners[lightboxIndex];
      lightboxImage.src = opener.dataset.fullImage;
      lightboxImage.alt = opener.dataset.imageAlt || '';
      if (lightboxCounter) lightboxCounter.textContent = `${lightboxIndex + 1} / ${imageOpeners.length}`;
    }

    function stepLightbox(dir) {
      showLightboxImage(lightboxIndex + dir);
    }

    if (lightbox && lightboxImage) {
      imageOpeners.forEach((opener, index) => {
        opener.addEventListener('click', () => {
          showLightboxImage(index);
          lightbox.showModal();
          document.body.classList.add('overflow-hidden');
        });
      });

      lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => lightbox.close());
      lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => stepLightbox(-1));
      lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => stepLightbox(1));

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target.classList.contains('product__lightbox-content')) lightbox.close();
      });
      lightbox.addEventListener('close', () => {
        document.body.classList.remove('overflow-hidden');
        lightboxImage.removeAttribute('src');
      });
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); stepLightbox(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); stepLightbox(1); }
      });

      let lightboxTouchStartX = 0;
      let lightboxTouchStartY = 0;
      lightbox.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        lightboxTouchStartX = event.touches[0].clientX;
        lightboxTouchStartY = event.touches[0].clientY;
      }, { passive: true });
      lightbox.addEventListener('touchend', (event) => {
        if (event.changedTouches.length !== 1 || imageOpeners.length < 2) return;
        const deltaX = event.changedTouches[0].clientX - lightboxTouchStartX;
        const deltaY = event.changedTouches[0].clientY - lightboxTouchStartY;
        if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        stepLightbox(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }

    const mediaMain = productEl.querySelector('.product__media-main');
    if (mediaMain && mediaItems.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;

      mediaMain.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }, { passive: true });

      mediaMain.addEventListener('touchend', (event) => {
        if (event.changedTouches.length !== 1) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        const deltaY = event.changedTouches[0].clientY - touchStartY;
        const swipeThreshold = Math.min(80, Math.max(40, mediaMain.clientWidth * 0.1));

        if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        stepMedia(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }
  }

  /* ---------- Facet auto-submit ---------- */
  const facetForm = document.querySelector('form[data-facet-form]');
  if (facetForm) {
    let debounce;
    facetForm.addEventListener('change', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => facetForm.submit(), 350);
    });
  }
})();
